/**
 * NIGHTBOUND - creature interaction copy.
 *
 * Tapping the creature produces a reaction that is shaped by its mood, its
 * bond with the player, and its sanity. `pickReaction` is pure and takes the
 * randomness as an argument, so it can be tested exactly.
 */

import { round2 } from './constants'
import type { SanityBand } from './mood'
import { sanityBand } from './mood'
import type { ActivityState, MoodId } from './species'

export interface ReactionContext {
  name: string
  mood: MoodId
  bond: number
  sanity: number
  activity: ActivityState
  sleeping: boolean
}

export interface Reaction {
  text: string
  bondDelta: number
  sanityDelta: number
  /** Short tag used to pick a matching creature animation. */
  tone: 'warm' | 'neutral' | 'cold' | 'unsettling'
}

type Pool = (name: string) => string

const POOLS: Record<MoodId, Pool[]> = {
  SLEEPING: [
    (n) => `${n} shifts in its sleep.`,
    (n) => `Something moves behind ${n}'s closed eyes.`,
    (n) => `${n} breathes slowly. It has not noticed you.`,
  ],
  HUNGRY: [
    (n) => `${n} seems hungry.`,
    (n) => `${n} sniffs toward the feeding corner.`,
    (n) => `${n} nudges your hand, then your pocket.`,
    (n) => `${n} is chewing at nothing.`,
  ],
  DIRTY: [
    (n) => `${n} is filthy. It leaves a dark mark on your sleeve.`,
    (n) => `${n} scratches at itself, irritated.`,
    (n) => `Something moves in ${n}'s fur.`,
  ],
  TIRED: [
    (n) => `${n} looks exhausted.`,
    (n) => `${n} doesn't seem interested.`,
    (n) => `${n} lies down, then stands up again.`,
  ],
  LONELY: [
    (n) => `${n} watches you carefully.`,
    (n) => `${n} does not come any closer.`,
    (n) => `${n} turns away when you reach for it.`,
  ],
  UNWELL: [
    (n) => `${n} twitches. Something is wrong with it.`,
    (n) => `${n} holds itself very still.`,
    (n) => `${n} shivers, though the room is not cold.`,
  ],
  HAPPY: [
    (n) => `${n} curls up beside you.`,
    (n) => `${n} presses into your hand.`,
    (n) => `${n} makes a low, contented sound.`,
    (n) => `${n} nudges your hand and stays there.`,
  ],
  CONTENT: [
    (n) => `${n} watches you.`,
    (n) => `${n} tilts its head at the sound of the pipes.`,
    (n) => `${n} settles against your leg.`,
    (n) => `${n} blinks slowly.`,
  ],
}

const BONDED_EXTRA: Pool[] = [
  (n) => `${n} knows your footsteps by now.`,
  (n) => `${n} puts its weight against you and stays.`,
  (n) => `${n} would follow you out of here if you let it.`,
]

const DISTANT_EXTRA: Pool[] = [
  (n) => `${n} keeps the length of the room between you.`,
  (n) => `${n} watches your hands, not your face.`,
]

const LOW_SANITY_EXTRA: Record<Exclude<SanityBand, 'STEADY'>, Pool[]> = {
  UNSETTLED: [
    (n) => `${n} stares at the corner for a moment too long.`,
    (n) => `${n} stops mid-motion and listens to nothing.`,
  ],
  DISTURBED: [
    (n) => `${n} stares at something behind you.`,
    (n) => `${n} has been in that corner for an hour.`,
    (n) => `${n} mimics a sound you did not make.`,
  ],
  FRACTURED: [
    (n) => `${n}'s eyes track something that is not there.`,
    (n) => `${n} is shaped slightly wrong in the low light, then is not.`,
    (n) => `${n} answers a question you did not ask.`,
    (n) => `Something else is looking out through ${n}'s eyes.`,
  ],
}

function pick<T>(list: T[], roll: number): T {
  const index = Math.floor(roll * list.length) % list.length
  return list[index]!
}

/**
 * Choose a reaction. `roll` should be a fresh random number in [0, 1).
 * `roll2` drives the optional unsettling/homebrew lines so a single roll can
 * never accidentally select two different tables.
 */
export function pickReaction(ctx: ReactionContext, roll: number, roll2 = 0.5): Reaction {
  const band = sanityBand(ctx.sanity)

  // Unsettling behaviour occasionally leaks through when sanity is low.
  if (band !== 'STEADY' && ctx.activity === 'IDLE' && roll2 < 0.35) {
    const text = pick(LOW_SANITY_EXTRA[band], roll)(ctx.name)
    return { text, bondDelta: 0.05, sanityDelta: -0.2, tone: 'unsettling' }
  }

  if (ctx.bond >= 80 && ctx.mood !== 'SLEEPING' && roll2 < 0.3) {
    return {
      text: pick(BONDED_EXTRA, roll)(ctx.name),
      bondDelta: 0.5,
      sanityDelta: 0.2,
      tone: 'warm',
    }
  }

  if (ctx.bond < 20 && ctx.mood !== 'HUNGRY' && roll2 < 0.35) {
    return {
      text: pick(DISTANT_EXTRA, roll)(ctx.name),
      bondDelta: 0.1,
      sanityDelta: 0,
      tone: 'cold',
    }
  }

  const text = pick(POOLS[ctx.mood], roll)(ctx.name)

  const table: Record<MoodId, { bond: number; sanity: number; tone: Reaction['tone'] }> = {
    HAPPY: { bond: 0.5, sanity: 0.3, tone: 'warm' },
    CONTENT: { bond: 0.35, sanity: 0.15, tone: 'warm' },
    HUNGRY: { bond: 0.2, sanity: 0, tone: 'neutral' },
    DIRTY: { bond: 0.05, sanity: 0, tone: 'neutral' },
    TIRED: { bond: 0.1, sanity: -0.05, tone: 'neutral' },
    LONELY: { bond: 0.2, sanity: 0.05, tone: 'cold' },
    UNWELL: { bond: 0.15, sanity: -0.1, tone: 'cold' },
    SLEEPING: { bond: 0.1, sanity: 0, tone: 'warm' },
  }

  const gains = table[ctx.mood]
  return {
    text,
    bondDelta: round2(gains.bond),
    sanityDelta: round2(gains.sanity),
    tone: gains.tone,
  }
}

/** Flavour lines shown when entering the shelter, by mood. */
export const SHELTER_GREETINGS: Record<MoodId, string[]> = {
  HAPPY: ['It lifts its head as the door opens.', 'It was waiting by the door.'],
  CONTENT: ['It watches you come down the stairs.', 'It has been awake a while.'],
  HUNGRY: ['It is already at the feeding corner.', 'It paces between you and the bowl.'],
  DIRTY: ['The room smells worse than it did.', 'It has not cleaned itself in a while.'],
  TIRED: ['It barely raises its head.', 'It has not moved from the bed.'],
  LONELY: ['It does not get up.', 'It looks at you the way strangers do.'],
  UNWELL: ['Something is wrong. You can hear it breathing.', 'It is lying very still.'],
  SLEEPING: ['It is asleep in the dark.', 'The room is quiet. It is still asleep.'],
}
